/**
 * FontConfigContext
 *
 * React context that distributes resolved font overrides
 * through the V2ScreenView component tree.
 *
 * At runtime, FunnelEngineV2 detects the platform, builds fallback chains
 * from the project's fontConfig, and provides them via this context.
 * V2 components consume it to pass fontOverrides to mapTypographyStyles().
 *
 * When no provider exists (editor preview, tests), the context returns
 * undefined — which makes mapTypographyStyles() use DEFAULT_FONT_FALLBACK_CHAINS.
 *
 * @module font-config/context
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';
import type { PlatformId, WebFontAsset } from '../types/fontConfig.types';
import { useEnsureWebFonts } from '../hooks/useEnsureWebFonts';

/**
 * Context value shape
 */
export interface FontConfigContextValue {
  /**
   * Resolved CSS font-family chains per schema font.
   * Example: { 'SF Pro': "'Roboto', sans-serif" }
   * undefined = use DEFAULT_FONT_FALLBACK_CHAINS
   */
  fontOverrides: Record<string, string> | undefined;

  /**
   * Detected platform (for UI display / debugging)
   */
  platform: PlatformId;

  /**
   * Active self-hosted web fonts for this surface. Consumed by renderers
   * that own a separate document (WebEmbed iframe) and must re-declare
   * the @font-face rules there.
   */
  webFonts?: WebFontAsset[];
}

const FontConfigContext = createContext<FontConfigContextValue | undefined>(undefined);

/**
 * Provider props
 */
export interface FontConfigProviderProps {
  children: ReactNode;
  fontOverrides: Record<string, string> | undefined;
  platform: PlatformId;
  /**
   * Self-hosted web fonts the surface needs (from collectActiveWebFonts).
   * When present, the provider injects their @font-face into document.head
   * via useEnsureWebFonts (idempotent, SSR-safe). Optional — existing
   * callers are unaffected.
   */
  webFonts?: WebFontAsset[];
}

/**
 * Provides font overrides to the V2ScreenView tree.
 */
export function FontConfigProvider({
  children,
  fontOverrides,
  platform,
  webFonts,
}: FontConfigProviderProps) {
  useEnsureWebFonts(webFonts);

  return (
    <FontConfigContext.Provider value={{ fontOverrides, platform, webFonts }}>
      {children}
    </FontConfigContext.Provider>
  );
}

const EMPTY_WEB_FONTS: WebFontAsset[] = [];

/**
 * Active web fonts for the current surface ([] without a provider).
 * Stable empty reference so consumers can memo on identity.
 */
export function useWebFontAssets(): WebFontAsset[] {
  const ctx = useContext(FontConfigContext);
  return ctx?.webFonts ?? EMPTY_WEB_FONTS;
}

/**
 * Hook to consume font overrides in V2 components.
 * Returns undefined when no provider is present (backward compat).
 */
export function useFontOverrides(): Record<string, string> | undefined {
  const ctx = useContext(FontConfigContext);
  return ctx?.fontOverrides;
}

/**
 * Hook to get the detected platform.
 * Returns 'ios' as default when no provider is present (design intent).
 */
export function usePlatform(): PlatformId {
  const ctx = useContext(FontConfigContext);
  return ctx?.platform ?? 'ios';
}
